/**
 * OPS-2858. Classifies a whole company/location SET for INTERNAL purposes only:
 * support-user access, client metrics, and internal feature testing. It must NOT
 * change how a company or location behaves.
 *
 * It lives on the Company; locations inherit it.
 *
 * MIRROR - NOT the canonical definition. The canonical one, with the full meaning of
 * each value and how it relates to isActive and isTest, is:
 *
 *   domains/platform/functions/company-functions/src/lib/models/org_sub_type.go
 *
 * There is no shared contracts package the three copies build against
 * (packages/api-contracts is an empty placeholder), so changing the value set means
 * changing all three - the third is
 * domains/infrastructure/tools/sys-admin-utilities/lib/models/companies/org-sub-type.go.
 */
export const OrgSubType = {
  /** The default. A company with no classification reads as this, never as "". */
  UNCLASSIFIED: "UNCLASSIFIED",
  /** A real client. Real data, feeds internal reporting, inside our SLAs. */
  CLIENT: "CLIENT",
  /** Fake but high-quality data for prospects. Client-priority issues, no reporting. */
  SALES_DEMO: "SALES_DEMO",
  /** Sandbox for internal client-facing teams. Low data quality by design. */
  INTERNAL_TRAINING: "INTERNAL_TRAINING",
  /** Engineering / product / QA post-deploy verification. Low issue priority. */
  INTERNAL_TEST: "INTERNAL_TEST",
};

/**
 * Display order for facets and badges: UNCLASSIFIED first, because while the ~50
 * companies are being classified by hand the most useful question is "what is left?".
 */
export const ORG_SUB_TYPES: string[] = [
  OrgSubType.UNCLASSIFIED,
  OrgSubType.CLIENT,
  OrgSubType.SALES_DEMO,
  OrgSubType.INTERNAL_TRAINING,
  OrgSubType.INTERNAL_TEST,
];

const ORG_SUB_TYPE_LABELS: { [code: string]: string } = {
  UNCLASSIFIED: "Unclassified",
  CLIENT: "Client",
  SALES_DEMO: "Sales/Demo",
  INTERNAL_TRAINING: "Internal Training",
  INTERNAL_TEST: "Internal Test",
};

export function isOrgSubType(value: string): boolean {
  return ORG_SUB_TYPES.indexOf(value) > -1;
}

/**
 * Normalises a value off the wire. Absent, empty, or unrecognised all resolve to
 * UNCLASSIFIED - the backend normalises too, so an unrecognised value reaching here
 * means the index is stale rather than that the user typed something odd.
 */
export function parseOrgSubType(value: string | undefined | null): string {
  return value && isOrgSubType(value) ? value : OrgSubType.UNCLASSIFIED;
}

/** The human-readable name. Never the raw code - "SALES_DEMO" is not a label. */
export function orgSubTypeLabel(value: string | undefined | null): string {
  return ORG_SUB_TYPE_LABELS[parseOrgSubType(value)];
}



// WEBPACK FOOTER //
// ./src/models/company-resources/org-sub-type.ts